import * as React from 'react';
import {
  InstantSearch,
  SearchBox,
  Configure,
  Pagination,
  useHits,
  useSearchBox,
} from 'react-instantsearch';
import Typography from '@mui/material/Typography';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Filter, FullTabInt, LoadedUser } from '../interfaces';
import { INDEX, searchClient } from '../algoliaconfig';
import MultiSelectFilter from '../components/filtering/MultiSelectFilter';
import { getCleanedPhone, important } from '../utils/global/helper';
import 'instantsearch.css/themes/satellite.css';
import TableTitle from '../components/table/TableTitle';
import FullPageModal from '../components/modal/FullPageModal';
import CustomerForm from '../components/form/CustomerForm';
import useUser from '../hooks/global/useUser';
import useRestrictedBusinessIds from '../hooks/global/useRestrictedBusinessIds';
import useFeatureGuard from '../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../types/subscriptions';
import useSubscriptionModal from '../hooks/subscriptions/useSubscriptionModal';
import PageLayout from '../components/global/PageLayout';
import CustomerSideBarModal from '../components/customers/CustomerSideBarModal';
import CustomerProfilePage from '../components/customers/profile/CustomerProfilePage';
import useBusinessWebRedesign from '../hooks/businesses/useBusinessWebRedesign';
import {
  getInitialArtistOptions,
  getInitialSelectedArtists,
} from '../helpers/employees';
import { applyAlgoliaRestriction, getFullName } from '../helpers/global';
import { FILTER_PERSISTENCE_KEYS, ROUTES } from '../constants/global';
import SearchRefreshWrapper from '../components/search/SearchRefreshWrapper';
import useSearchRefresh from '../hooks/search/useSearchRefresh';
import TabBar from '../components/filtering/TabBar';
import usePersistedTab from '../hooks/global/usePersistedTab';
import useSearchGuests from '../hooks/customers/useSearchGuests';
import ColoredAlert from '../components/global/ColoredAlert';
import VirtualizedTable from '../components/table/VirtualizedTable';
import { Columns, DataFromColumns } from '../types/reports';

const ARTIST_FILTER_NAME = FILTER_PERSISTENCE_KEYS.customerArtists;

function HitComponent(props: any) {
  const { openCustomerModal } = props;
  const { hits, results } = useHits(props);

  return (
    <div className="table">
      <TableTitle
        name="Customers"
        showing={results?.hits.length || 0}
        total={results?.nbHits || 0}
      />
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell component="th" scope="header">
                Name
              </TableCell>
              <TableCell component="th" scope="header">
                Phone Number
              </TableCell>
              <TableCell component="th" scope="header">
                Email
              </TableCell>
              <TableCell component="th" scope="header">
                View Details
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {hits.map(hit => (
              <TableRow key={hit.customerId as string}>
                <TableCell>
                  <Typography variant="body1">
                    {hit.fullName as string}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">
                    {getCleanedPhone(hit.phoneNumber?.toString() ?? '')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body1">{hit.email as string}</Typography>
                </TableCell>
                <TableCell>
                  <Button
                    onClick={() => openCustomerModal(hit.customerId)}
                    variant="outlined"
                    className="table-button">
                    View Customer
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination />
    </div>
  );
}

function GuestsHitComponent({
  openCustomerModal,
  employeeIds,
}: {
  openCustomerModal: (customerId: string) => void;
  employeeIds?: string[];
}) {
  const { query } = useSearchBox();
  const { data: guests, isFetching } = useSearchGuests(query, {
    employeeIds,
    allowEmptyQuery: true,
  });

  const rows = React.useMemo(
    () =>
      (guests ?? []).map(guest => ({
        name: getFullName({
          firstName: guest.firstName,
          lastName: guest.lastName,
        }),
        accountHolder: getFullName({
          firstName: guest.customer.user.firstName,
          lastName: guest.customer.user.lastName,
        }),
        button: {
          button: {
            label: 'View Customer',
            onClick: () => openCustomerModal(guest.customerId),
            disabled: false,
          },
        },
        customerId: guest.customerId,
      })),
    [guests, openCustomerModal],
  );

  const columns = [
    { dataKey: 'name', label: 'Name', width: 'auto' },
    { dataKey: 'accountHolder', label: 'Account Holder', width: 'auto' },
    { dataKey: 'button', label: '', width: 224, hidden: !rows.length },
  ] as const satisfies Columns;

  return (
    <>
      <ColoredAlert
        persistentDismissAlertKey="customers-guests-tab-info"
        persistentDismissStorage={localStorage}
        type="info"
        title="What are guests?"
        subtitle="Guests are people booked under someone else's account, whether that's a friend or family member being booked for, or someone who booked on another person's phone. A guest doesn't have their own Porter account, but they do have their own details and waivers, stored on the account holder's customer profile."
        animate={false}
        containerSx={{ mb: 3 }}
      />
      <VirtualizedTable
        type="regular"
        columns={columns}
        data={rows as unknown as DataFromColumns<typeof columns>[]}
        height={480}
        isLoading={isFetching && !rows.length}
        emptyText="No guests to show right now. If you believe this is an error, please reach out to our support team."
        headerContent={{
          title: 'Guests',
          subtitle: rows.length ? `${rows.length} guests to show` : undefined,
        }}
        onRowClick={row =>
          openCustomerModal((row as (typeof rows)[number]).customerId)
        }
      />
    </>
  );
}

enum Tab {
  CUSTOMERS,
  GUESTS,
}

export default function CustomersPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const { active: redesignActive } = useBusinessWebRedesign();
  const { user: loadedUser } = useUser<LoadedUser>();
  const restrictedBusinessIds = useRestrictedBusinessIds();

  const [newCustomerModalOpen, setNewCustomerModalOpen] = React.useState(false);
  const [shouldScrollToTop, setShouldScrollToTop] = React.useState(false);

  const [openedCustomerId, setOpenedCustomerId] = React.useState<string | null>(
    null,
  );

  const artistOptions = React.useMemo(() => {
    if (!loadedUser) return [];
    return getInitialArtistOptions(loadedUser);
  }, [loadedUser]);

  const [selectedArtists, setSelectedArtists] = React.useState(() =>
    getInitialSelectedArtists(loadedUser, artistOptions, ARTIST_FILTER_NAME),
  );
  const artistFilter = React.useMemo<Filter>(
    () => ({
      name: ARTIST_FILTER_NAME,
      options: artistOptions,
      state: selectedArtists,
      setState: setSelectedArtists,
      emptyLabel: 'Select an artist',
      allLabel: 'All artists',
    }),
    [artistOptions, selectedArtists],
  );

  const { openSubscriptionModal } = useSubscriptionModal();
  const canManageCustomers = useFeatureGuard(Feature.MANAGE_CUSTOMER);
  const canAddCustomers = useFeatureGuard(Feature.ADD_CUSTOMER);

  // Set up click action for each row of the customers table
  const openCustomerModal = React.useCallback(
    async (tempCustomerId: string) => {
      if (!loadedUser?.business.id) return;
      if (!canManageCustomers) {
        openSubscriptionModal();
        return;
      }

      setOpenedCustomerId(tempCustomerId);
      if (redesignActive && customerId !== tempCustomerId) {
        navigate(`${ROUTES.customers}/${tempCustomerId}`);
      }
    },
    [
      canManageCustomers,
      customerId,
      loadedUser,
      navigate,
      openSubscriptionModal,
      redesignActive,
    ],
  );

  const closeCustomerProfile = React.useCallback(() => {
    setOpenedCustomerId(null);
    navigate(returnTo || ROUTES.customers);
  }, [navigate, returnTo]);

  React.useEffect(() => {
    if (customerId) {
      openCustomerModal(customerId);
    } else {
      setOpenedCustomerId(null);
    }
  }, [customerId, openCustomerModal]);

  // Close edit appointment
  const closeNewCustomer = React.useCallback(() => {
    setNewCustomerModalOpen(false);
  }, []);

  const algoliaFilters = React.useMemo(() => {
    const shopFilterString = `businessId:${loadedUser?.business.id}`;
    const artistFilterString = `employeeId:${loadedUser?.userId}`;
    const restrict = (filter: string) =>
      applyAlgoliaRestriction(filter, restrictedBusinessIds);

    if (!loadedUser?.canViewAllShopCustomers) {
      return restrict(artistFilterString);
    }

    let otherArtistsString = '';
    if (
      selectedArtists &&
      selectedArtists.length > 0 &&
      selectedArtists[0] !== 'Initialize' &&
      selectedArtists.length !== artistFilter?.options.length
    ) {
      otherArtistsString += '(';
      selectedArtists.forEach((artist, index) => {
        otherArtistsString += `employeeId:${artist}`;
        if (selectedArtists[index + 1]) {
          otherArtistsString += ` OR `;
        } else {
          otherArtistsString += ')';
        }
      });
    }

    if (otherArtistsString) {
      return restrict(`${shopFilterString} AND ${otherArtistsString}`);
    }
    return restrict(`${shopFilterString} OR ${artistFilterString}`);
  }, [
    artistFilter?.options.length,
    loadedUser?.business.id,
    loadedUser?.userId,
    selectedArtists,
    loadedUser?.canViewAllShopCustomers,
    restrictedBusinessIds,
  ]);

  const openCustomerForm = () => {
    if (!canAddCustomers) {
      openSubscriptionModal();
      return;
    }
    setNewCustomerModalOpen(true);
  };

  // Create a ref to store the refresh function
  const { refreshRef, refresh: refreshSearch } = useSearchRefresh();

  const handleCustomerSave = () => {
    // Refresh the search results to include any new/updated customers
    refreshSearch();
  };

  // Mirror the Algolia artist filter: only send when a real subset is selected
  const guestEmployeeIds = React.useMemo(() => {
    if (
      !selectedArtists?.length ||
      selectedArtists[0] === 'Initialize' ||
      selectedArtists.length === artistFilter?.options.length
    ) {
      return undefined;
    }
    return selectedArtists;
  }, [artistFilter?.options.length, selectedArtists]);

  const tabs = React.useMemo<FullTabInt[]>(
    () => [
      {
        id: 'customers',
        tabId: Tab.CUSTOMERS,
        label: 'Customers',
        component: <HitComponent openCustomerModal={openCustomerModal} />,
      },
      {
        id: 'guests',
        tabId: Tab.GUESTS,
        label: 'Guests',
        component: (
          <GuestsHitComponent
            openCustomerModal={openCustomerModal}
            employeeIds={guestEmployeeIds}
          />
        ),
      },
    ],
    [guestEmployeeIds, openCustomerModal],
  );

  const [tab, onChangeTab] = usePersistedTab({
    defaultTab: Tab.CUSTOMERS,
    tabs,
  });

  if (redesignActive && openedCustomerId) {
    return (
      <CustomerProfilePage
        customerId={openedCustomerId}
        onBack={closeCustomerProfile}
      />
    );
  }

  return (
    <InstantSearch searchClient={searchClient} indexName={INDEX} insights>
      <Configure filters={algoliaFilters} distinct />

      <SearchRefreshWrapper refreshRef={refreshRef}>
        <PageLayout title="Customers">
          {tab === Tab.CUSTOMERS && (
            <Box
              className="header-right"
              sx={{
                display: {
                  xs: 'inline',
                  md: 'none',
                },
              }}>
              <Button
                variant="contained"
                className="primary"
                onClick={openCustomerForm}>
                Add New
              </Button>
            </Box>
          )}
          <div className="customers-header">
            <div className="header-left">
              <SearchBox
                placeholder={
                  tab === Tab.GUESTS ? 'Search guests' : 'Search customers'
                }
                searchAsYouType
              />
              {artistFilter && (
                <MultiSelectFilter
                  name={artistFilter.name}
                  options={artistFilter.options}
                  state={artistFilter.state}
                  setState={artistFilter.setState}
                  emptyLabel={artistFilter.emptyLabel}
                  allLabel={artistFilter.allLabel}
                />
              )}
            </div>
            {tab === Tab.CUSTOMERS && (
              <Box
                className="header-right"
                sx={{
                  display: {
                    xs: important('none'),
                    md: important('inline'),
                  },
                }}>
                <Button
                  variant="contained"
                  className="primary"
                  onClick={openCustomerForm}>
                  Add New
                </Button>
              </Box>
            )}
          </div>
          <TabBar
            tabs={tabs}
            currentTab={tab}
            changeTab={onChangeTab}
            noTopMargin
            marginTop={3}
          />
        </PageLayout>
      </SearchRefreshWrapper>

      <CustomerSideBarModal
        customerId={openedCustomerId}
        isOpen={!!openedCustomerId}
        onClose={closeCustomerProfile}
        onCustomerSave={handleCustomerSave}
      />

      <FullPageModal
        modalOpen={newCustomerModalOpen}
        setModalOpen={setNewCustomerModalOpen}
        handleClose={closeNewCustomer}
        shouldScrollTop={shouldScrollToTop}
        setShouldScrollToTop={setShouldScrollToTop}
        name="Add Customer">
        <CustomerForm
          setModalOpen={setNewCustomerModalOpen}
          setShouldScrollToTop={setShouldScrollToTop}
          onCustomerSave={handleCustomerSave}
        />
      </FullPageModal>
    </InstantSearch>
  );
}
